import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, X } from 'lucide-react';
import { spore } from '@/spore/runtime';
import { TOUCH_CALIBRATION_STEPS as CALIBRATION_STEPS, computeTouchCalibration } from '@/spore/calibration';

const PREP_MS = 1500;

const Ring = ({ progress, seconds }) => {
  const r = 120;
  const c = 2 * Math.PI * r;
  return (
    <svg width="280" height="280" viewBox="0 0 280 280" className="mx-auto">
      <circle cx="140" cy="140" r={r} stroke="rgba(0,255,157,0.12)" strokeWidth="4" fill="none" />
      <circle className="countdown-ring" cx="140" cy="140" r={r} stroke="#00ff9d" strokeWidth="4" fill="none" strokeDasharray={c} strokeDashoffset={c * (1 - progress)} transform="rotate(-90 140 140)" style={{ filter: 'drop-shadow(0 0 10px #00ff9d)' }} />
      <text data-testid="calibration-countdown" x="140" y="160" textAnchor="middle" fill="#e8f1ee" fontSize="72" fontFamily="Syne" fontWeight="800">{seconds}</text>
    </svg>
  );
};

const Summary = ({ record, onRedo, onClose }) => (
  <div data-testid="calibration-summary" className="mx-auto max-w-3xl">
    <h2 className="font-display text-4xl font-extrabold">{record.ok ? <span className="glow-spore">Calibration saved</span> : <span className="glow-amber">Saved with safe defaults</span>}</h2>
    {record.warnings.map((w) => (
      <p key={w} data-testid="calibration-warning" className="mt-4 flex gap-3 border border-[rgba(255,176,0,0.4)] bg-[rgba(255,176,0,0.06)] p-3 text-[var(--amber)]"><AlertTriangle className="shrink-0" size={18} /> {w}</p>
    ))}
    {record.ok && !record.warnings.length && <p className="mt-4 flex items-center gap-3 text-[var(--spore)]"><CheckCircle2 size={18} /> Gentle and firm touch separated cleanly.</p>}
    <div className="mt-6 grid grid-cols-2 gap-6 text-sm md:grid-cols-3">
      {record.rows.map(([k, v]) => (
        <div key={k} className="border-l border-[var(--hair-strong)] pl-3">
          <div className="label-eyebrow">{k}</div>
          <div className="font-display text-2xl font-bold tabular-nums">{v ?? '—'}</div>
        </div>
      ))}
    </div>
    <div className="mt-8 flex gap-4">
      <button data-testid="calibration-redo-button" className="btn-lab" onClick={onRedo}>Redo calibration</button>
      <button data-testid="calibration-close-button" className="btn-lab primary" onClick={onClose}>Done</button>
    </div>
  </div>
);

export const CalibrationOverlay = ({ onClose }) => {
  const [phase, setPhase] = useState('intro');
  const [prep, setPrep] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [events, setEvents] = useState(0);
  const [record, setRecord] = useState(null);
  const buffers = useRef({ still: [], taps: [], stroke: [] });
  const recording = useRef(null);

  useEffect(() => spore.subscribe((e) => {
    if (!recording.current) return;
    buffers.current[recording.current].push(e);
    setEvents((n) => n + 1);
  }), []);

  useEffect(() => {
    if (typeof phase !== 'number') return undefined;
    const step = CALIBRATION_STEPS[phase];
    let start = performance.now();
    let prepping = true;
    setPrep(true);
    setEvents(0);
    const id = setInterval(() => {
      const el = performance.now() - start;
      if (prepping) {
        if (el >= PREP_MS) {
          prepping = false;
          setPrep(false);
          start = performance.now();
          recording.current = step.id;
        }
        setRemaining(step.seconds * 1000);
        return;
      }
      const left = step.seconds * 1000 - el;
      setRemaining(Math.max(0, left));
      if (left <= 0) {
        clearInterval(id);
        recording.current = null;
        if (phase < CALIBRATION_STEPS.length - 1) setPhase(phase + 1);
        else {
          const result = computeTouchCalibration(buffers.current);
          const b = buffers.current;
          spore.uploadRecording('calibration', { events: [...b.still, ...b.taps, ...b.stroke], meta: { ok: result.ok, warnings: result.warnings, rows: result.rows, phases: { still: b.still.length, taps: b.taps.length, stroke: b.stroke.length } } });
          if (result.baseline) spore.touch.setBaseline(result.baseline);
          if (result.baseline) localStorage.setItem('spore.baseline.v1', JSON.stringify(result.baseline));
          spore.applyTouchConfig(result.config);
          setRecord(result);
          setPhase('summary');
        }
      }
    }, 100);
    return () => {
      clearInterval(id);
      recording.current = null;
    };
  }, [phase]);

  const begin = () => {
    buffers.current = { still: [], taps: [], stroke: [] };
    setRecord(null);
    setPhase(0);
  };

  const step = typeof phase === 'number' ? CALIBRATION_STEPS[phase] : null;
  return createPortal(
    <div data-testid="calibration-modal" data-phase={String(phase)} className="lab-bg fixed inset-0 z-50 flex items-center justify-center bg-[#050807]/95 px-8 backdrop-blur-xl">
      <button data-testid="calibration-abort-button" className="btn-lab absolute right-8 top-8 flex items-center gap-2" onClick={onClose}><X size={16} /> {phase === 'summary' ? 'Close' : 'Abort'}</button>
      <div className="relative z-10 w-full">
        {phase === 'intro' && (
          <div className="mx-auto max-w-3xl">
            <div className="label-eyebrow mb-4">[ SPECIMEN: THE SPORE<span className="normal-case">s</span> ] · CALIBRATION PROTOCOL</div>
            <h2 className="font-display text-5xl font-extrabold">Three steps. ~20 seconds.</h2>
            <ol className="mt-8 space-y-3 text-lg">
              {CALIBRATION_STEPS.map((s, i) => (
                <li key={s.id} className="flex gap-4"><span className="glow-spore">0{i + 1}</span> {s.title} <span className="text-[var(--muted)]">· {s.seconds}s</span></li>
              ))}
            </ol>
            <button data-testid="calibration-begin-button" className="btn-lab primary mt-10 text-base" onClick={begin}>Begin calibration</button>
          </div>
        )}
        {step && (
          <div data-testid={`calibration-step-${step.id}`} className="text-center">
            <div className="label-eyebrow mb-3">STEP 0{phase + 1} / 03 {prep ? '· GET READY' : '· RECORDING'}</div>
            <h2 className={`font-display text-5xl font-extrabold sm:text-6xl lg:text-7xl ${prep ? 'text-[var(--muted)]' : 'glow-spore'}`}>{step.title}</h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-[var(--muted)]">{step.detail}</p>
            <div className="mt-8">
              <Ring progress={prep ? 0 : 1 - remaining / (step.seconds * 1000)} seconds={Math.ceil(remaining / 1000)} />
            </div>
            <div data-testid="calibration-event-count" className="mt-4 text-sm text-[var(--teal)]">{events} events captured</div>
          </div>
        )}
        {phase === 'summary' && record && <Summary record={record} onRedo={begin} onClose={onClose} />}
      </div>
    </div>,
    document.body
  );
};
